import { useTranslate } from 'react-admin';
import { useEffect } from 'react';
import { INTL_COUNT } from '../../../../common/constants';
import {
  convertToKeenFriendlyHotelTimeframe,
  getDaysFromTimeframe,
  useKeenQuery,
} from '../../../../utils/keen';
import { useIntl } from '../../../../context/intlContext';
import {
  CategoryRecord,
  HotelRecord,
  Timeframe,
} from '../../../../common/types';
import { UsageTotalsKeenChartProps } from '../../UsageTotalsKeenChart';

const useOrderWeekdayAverageData = ({
  hotel,
  timeframe,
  selectedCategory,
}: {
  hotel: HotelRecord;
  timeframe: Timeframe;
  selectedCategory: CategoryRecord | null;
}) => {
  const intl = useIntl();
  const t = useTranslate();
  const daysInTimeFrame = getDaysFromTimeframe(timeframe);

  const [
    { data: ordersWeekday = [], loading: isOrdersWeekdayLoading },
    queryOrdersWeekday,
  ] = useKeenQuery<{ dateDayOfWeek: number; result: number }[]>();

  useEffect(() => {
    queryOrdersWeekday({
      analysisType: 'count',
      eventCollection: 'orders',
      groupBy: 'date.day_of_week',
      filters: selectedCategory
        ? [
            {
              propertyName: 'hotel_id',
              operator: 'eq',
              propertyValue: hotel.id,
            },
            {
              propertyName: 'test_order',
              operator: 'eq',
              propertyValue: false,
            },
            {
              propertyName: 'category_id',
              operator: 'eq',
              propertyValue: selectedCategory.id,
            },
          ]
        : [
            {
              propertyName: 'hotel_id',
              operator: 'eq',
              propertyValue: hotel.id,
            },
            {
              propertyName: 'test_order',
              operator: 'eq',
              propertyValue: false,
            },
          ],
      timeframe:
        typeof timeframe === 'string'
          ? timeframe
          : convertToKeenFriendlyHotelTimeframe(timeframe, hotel.tzInfoId),
      timezone: hotel.tzInfoId,
    });
  }, [
    hotel.id,
    queryOrdersWeekday,
    timeframe,
    selectedCategory,
    hotel.tzInfoId,
  ]);

  const dayToDataMap = ordersWeekday.reduce(
    (
      acc: { [key: number]: { dateDayOfWeek: number; result: number } },
      val: { dateDayOfWeek: number; result: number }
    ) => {
      acc[val.dateDayOfWeek] = val;
      return acc;
    },
    {}
  );
  const zeroFilledOrderWeekday = [...Array(7)].map(
    (_, index) => dayToDataMap[index] || { dateDayOfWeek: index, result: 0 }
  );

  const orderWeekdayAverage = zeroFilledOrderWeekday.map(
    ({ dateDayOfWeek, result }) => ({
      dateDayOfWeek,
      result: +(result / daysInTimeFrame).toFixed(2),
    })
  );

  const ordersWeekdayAverageData = orderWeekdayAverage.map(
    ({ dateDayOfWeek, result }) => ({
      id: t(`settings.day-of-week.${dateDayOfWeek}`, {
        count: INTL_COUNT.OTHER,
      }),
      value: result,
      tooltip: {
        title: t(`settings.day-of-week.${dateDayOfWeek}`, {
          count: INTL_COUNT.OTHER,
        }),
        subtitle: intl.formatNumber(result, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      },
    })
  );

  const chartProps: UsageTotalsKeenChartProps = {
    data: ordersWeekdayAverageData,
    color: '#7fa6db',
    skipEvenBottomLabels: false,
  };

  return {
    chartProps,
    loading: isOrdersWeekdayLoading,
  };
};

export default useOrderWeekdayAverageData;
